/**
 * Fotos ohne Projekt hochladen — mit Vorabprüfung, Fortschritt und Bericht.
 *
 * Benjamin, 13.10.2026: „Die Bilder sollen erst einfach hochgeladen werden, der
 * Fortschritt muss zu sehen sein, nach dem Upload ein Bericht.“ Und: 65 Fotos
 * brauchten eine Viertelstunde, weil die Leitung der Engpass ist. Deshalb:
 *
 * 1. Prüfsumme (SHA-256) im Browser, dann `POST /fotos/pruefen` — was Kontor schon
 *    hat, wird gar nicht erst gesendet.
 * 2. Ein Foto je Anfrage, zwei gleichzeitig, per XMLHttpRequest — nur so meldet der
 *    Browser, wie viele Bytes schon unterwegs sind.
 * 3. Jede Antwort fließt in einen Bericht: welches Projekt wie viele, was offen
 *    blieb, was abgelehnt wurde und warum.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiPost, fehlerText } from "@vimaana/ui";
import type { EingangAbgelehnt, EingangErgebnis, EingangZugeordnet } from "../hooks/useMedien";

const GLEICHZEITIG = 2;
const PRUEF_SCHWUNG = 50;

export type Bericht = {
  /** Fotos, die durchgelaufen sind (auch abgelehnte). */
  fotos: number;
  zugeordnet: EingangZugeordnet[];
  /** Projekte, die Kontor erkannt hat — Vorschläge im Stapel. */
  erkannt: EingangZugeordnet[];
  zuordnen: number;
  abgelehnt: EingangAbgelehnt[];
};

export type Lauf = {
  laeuft: boolean;
  gesamt: number;
  fertig: number;
  /** Noch ohne Prüfsumme — die Vorabprüfung läuft. */
  pruefen: number;
  /** Nur was wirklich gesendet wird — daran hängt die Restzeit. */
  bytesGesamt: number;
  bytesFertig: number;
  start: number;
  /** Nach „Abbrechen“: gesamt − fertig blieb ungesendet. */
  abgebrochen: boolean;
  bericht: Bericht;
};

const leererBericht = (): Bericht => ({ fotos: 0, zugeordnet: [], erkannt: [], zuordnen: 0, abgelehnt: [] });

function zusammen(liste: EingangZugeordnet[], neu: EingangZugeordnet[] | undefined): EingangZugeordnet[] {
  const aus = liste.map((z) => ({ ...z, ids: [...(z.ids ?? [])] }));
  for (const z of neu ?? []) {
    const da = aus.find((g) => g.projekt_id === z.projekt_id);
    if (da) { da.anzahl += z.anzahl; da.ids.push(...(z.ids ?? [])); }
    else aus.push({ ...z, ids: [...(z.ids ?? [])] });
  }
  return aus.sort((x, y) => y.anzahl - x.anzahl);
}

async function pruefsumme(f: File): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null;
  try {
    const h = await crypto.subtle.digest("SHA-256", await f.arrayBuffer());
    return Array.from(new Uint8Array(h), (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

function senden(f: File, bytes: (n: number) => void, merken: (x: XMLHttpRequest | null) => void): Promise<EingangErgebnis> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    merken(xhr);
    xhr.open("POST", "/api/kontor/fotos/eingang");
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => bytes(Math.min(e.loaded, f.size));
    xhr.onload = () => {
      merken(null);
      let body: unknown = null;
      try { body = JSON.parse(xhr.responseText); } catch { body = xhr.responseText; }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(body as EingangErgebnis);
      if (xhr.status === 401 && window.location.pathname !== "/login") window.location.replace("/login");
      reject({ status: xhr.status, body });
    };
    xhr.onerror = () => { merken(null); reject(new Error("Verbindung abgebrochen")); };
    xhr.onabort = () => { merken(null); reject(new DOMException("abgebrochen", "AbortError")); };
    const form = new FormData();
    form.append("datei", f);
    xhr.send(form);
  });
}

function einfliessen(b: Bericht, e: EingangErgebnis): Bericht {
  return {
    ...b,
    fotos: b.fotos + 1,
    zugeordnet: zusammen(b.zugeordnet, e.zugeordnet),
    erkannt: zusammen(b.erkannt, e.erkannt),
    zuordnen: b.zuordnen + e.zuordnen,
    abgelehnt: [...b.abgelehnt, ...e.abgelehnt],
  };
}

export function useFotoHochladen() {
  const qc = useQueryClient();
  const [lauf, setLauf] = useState<Lauf | null>(null);
  const schlange = useRef<File[]>([]);
  const aktiv = useRef(0);
  const pruefend = useRef(0);
  const stop = useRef(false);
  const unterwegs = useRef(new Map<File, XMLHttpRequest>());
  const teilBytes = useRef(new Map<File, number>());

  const abschliessen = useCallback(() => {
    if (aktiv.current || pruefend.current || schlange.current.length) return;
    setLauf((l) => (l && l.laeuft ? { ...l, laeuft: false } : l));
    qc.invalidateQueries({ queryKey: ["fotos", "stapel"] });
    qc.invalidateQueries({ queryKey: ["projekt"] });
  }, [qc]);

  const pumpen = useCallback(() => {
    while (!stop.current && aktiv.current < GLEICHZEITIG && schlange.current.length) {
      const f = schlange.current.shift()!;
      aktiv.current += 1;
      senden(
        f,
        (n) => {
          const vorher = teilBytes.current.get(f) ?? 0;
          teilBytes.current.set(f, n);
          setLauf((l) => (l ? { ...l, bytesFertig: l.bytesFertig + (n - vorher) } : l));
        },
        (x) => { if (x) unterwegs.current.set(f, x); else unterwegs.current.delete(f); },
      )
        .then((e) => setLauf((l) => (l ? { ...l, fertig: l.fertig + 1, bericht: einfliessen(l.bericht, e) } : l)))
        .catch((err) => {
          if ((err as DOMException)?.name === "AbortError") return;
          const grund = fehlerText(err, "Verbindung abgebrochen — dieses Foto bitte noch einmal hochladen");
          setLauf((l) => (l ? {
            ...l, fertig: l.fertig + 1,
            bericht: { ...l.bericht, fotos: l.bericht.fotos + 1, abgelehnt: [...l.bericht.abgelehnt, { datei: f.name, art: "fehler", grund }] },
          } : l));
        })
        .finally(() => {
          // Der Rest zur Dateigröße, falls der Browser das letzte Stück nicht gemeldet hat.
          const gemeldet = teilBytes.current.get(f) ?? 0;
          teilBytes.current.delete(f);
          if (gemeldet < f.size && !stop.current) setLauf((l) => (l ? { ...l, bytesFertig: l.bytesFertig + (f.size - gemeldet) } : l));
          aktiv.current -= 1;
          pumpen();
          abschliessen();
        });
    }
  }, [abschliessen]);

  const hinzufuegen = useCallback(async (dateien: File[]) => {
    if (!dateien.length) return;
    stop.current = false;
    const groesse = dateien.reduce((n, f) => n + f.size, 0);
    setLauf((l) => (l && l.laeuft
      ? { ...l, gesamt: l.gesamt + dateien.length, pruefen: l.pruefen + dateien.length, bytesGesamt: l.bytesGesamt + groesse }
      : { laeuft: true, gesamt: dateien.length, fertig: 0, pruefen: dateien.length, bytesGesamt: groesse, bytesFertig: 0,
          start: Date.now(), abgebrochen: false, bericht: leererBericht() }));
    pruefend.current += dateien.length;
    for (let i = 0; i < dateien.length; i += PRUEF_SCHWUNG) {
      if (stop.current) break;
      const teil = dateien.slice(i, i + PRUEF_SCHWUNG);
      const summen = await Promise.all(teil.map(pruefsumme));
      let vorhanden: Record<string, string> = {};
      const bekannt = summen.filter((s): s is string => !!s);
      if (bekannt.length) {
        try {
          vorhanden = (await apiPost<{ vorhanden: Record<string, string> }>("/api/kontor/fotos/pruefen", { summen: bekannt })).vorhanden ?? {};
        } catch {
          vorhanden = {}; // dann prüft eben der Server beim Hochladen
        }
      }
      if (stop.current) break; // abgebrochen, während geprüft wurde
      const doppelt: EingangAbgelehnt[] = [];
      let doppeltBytes = 0;
      teil.forEach((f, j) => {
        const grund = summen[j] ? vorhanden[summen[j]!] : undefined;
        if (grund) { doppelt.push({ datei: f.name, art: "doppelt", grund }); doppeltBytes += f.size; }
        else if (!stop.current) schlange.current.push(f);
      });
      pruefend.current = Math.max(0, pruefend.current - teil.length);
      setLauf((l) => (l ? {
        ...l, pruefen: Math.max(0, l.pruefen - teil.length), fertig: l.fertig + doppelt.length,
        bytesGesamt: l.bytesGesamt - doppeltBytes,
        bericht: { ...l.bericht, fotos: l.bericht.fotos + doppelt.length, abgelehnt: [...l.bericht.abgelehnt, ...doppelt] },
      } : l));
      pumpen();
    }
    abschliessen();
  }, [pumpen, abschliessen]);

  const abbrechen = useCallback(() => {
    stop.current = true;
    schlange.current = [];
    for (const x of unterwegs.current.values()) x.abort();
    unterwegs.current.clear();
    aktiv.current = 0;
    pruefend.current = 0;
    setLauf((l) => (l ? { ...l, laeuft: false, pruefen: 0, abgebrochen: true } : l));
    qc.invalidateQueries({ queryKey: ["fotos", "stapel"] });
  }, [qc]);

  const zuruecksetzen = useCallback(() => setLauf((l) => (l && !l.laeuft ? null : l)), []);

  // Wer den Tab während des Hochladens schließt, verliert den Rest — der Browser fragt nach.
  const laeuft = !!lauf?.laeuft;
  useEffect(() => {
    if (!laeuft) return;
    const warnen = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warnen);
    return () => window.removeEventListener("beforeunload", warnen);
  }, [laeuft]);

  return { lauf, hinzufuegen, abbrechen, zuruecksetzen };
}

/** „noch etwa 6 Min.“ — erst, wenn genug gemessen ist. */
export function restzeit(l: Lauf): string {
  const sek = (Date.now() - l.start) / 1000;
  if (sek < 6 || l.bytesFertig < 200_000) return "";
  const rate = l.bytesFertig / sek;
  const rest = Math.max(0, l.bytesGesamt - l.bytesFertig) / rate;
  if (rest < 45) return "gleich fertig";
  const min = Math.round(rest / 60);
  return min <= 1 ? "noch etwa 1 Min." : `noch etwa ${min} Min.`;
}

export function mb(bytes: number): string {
  return (bytes / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: bytes < 10_000_000 ? 1 : 0 });
}
